import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { User } from '../model/user';
import { AppService } from './app.service';
import { AliasSessionService } from './alias-session.service';
import { hasSessionHint } from '../helpers/cookie.util';
import { setStoredUser } from '../helpers/user-storage.util';

/**
 * connect-service's `ApiResponse` envelope as the password-reset routes answer it.
 * They always answer HTTP 200 and carry the outcome in `code` (200, 400, 409 or
 * 410) and `status`; `data` is an empty string unless the route returns a value.
 */
export interface PasswordResetResponse<T = never> {
  code: number;
  status: 'SUCCESS' | 'FAILURE';
  message: string;
  data: T | '';
}

/** What a successful redeem returns: the account's address, for the login page. */
export interface RedeemedAccount {
  email: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  constructor(
    private http: HttpClient,
    private appService: AppService,
    private aliasSessionService: AliasSessionService
  ) {}

  public login(
    email: string,
    password: string,
    keepMeSignedIn: boolean = false
  ): Observable<any> {
    return this.http.post(
      environment.apiUrl + '/user/login',
      {
        email: email?.toLowerCase(),
        password: password,
        keepMeSignedIn: keepMeSignedIn,
      },
      { responseType: 'json' }
    );
  }

  public register(register: User): Observable<any> {
    const smsOptIn = !!register.usePhoneForSms;
    return this.http.post<User>(environment.apiUrl + '/user/register', {
      firstName: register.firstName,
      lastName: register.lastName,
      password: register.password,
      email: register.email?.toLowerCase(),
      phone: register.phone,
      location: register.location,
      usePhoneForSms: smsOptIn,
      notifyBySms: smsOptIn,
      referredBy: register.referredBy,
    });
  }

  /**
   * Cheap synchronous session check backed by the readable `connect_auth` hint
   * cookie — see {@link hasSessionHint}, the shared predicate the auth
   * interceptor's refresh gate and the bootstrap profile clear also use. This is
   * a hint only — presence does not prove the access token is still valid (that
   * is enforced server-side; an expired token yields a 401, which the auth
   * interceptor handles via refresh/logout). Route guards should confirm the
   * session with {@link me}.
   */
  public isLoggedIn(): boolean {
    return hasSessionHint();
  }

  /**
   * Confirms the session against the server and returns the authenticated
   * principal's safe profile (no token/secret material). Caches the profile
   * into shared user storage so the existing identity/role readers
   * (`getStoredUser()`) stay in sync — the JWT is httpOnly, so the server is now
   * the only source of the current user's identity and role.
   */
  public me(): Observable<User> {
    return this.http
      .get<{ data: User; isAliasSession?: boolean; vendorName?: string | null; staffName?: string | null }>(
        environment.apiUrl + '/user/me',
        { responseType: 'json' }
      )
      .pipe(
        // The alias-session fields are siblings of `data` on the envelope, not on the
        // profile itself — read them before the `map` below unwraps to `data`.
        tap((envelope) => this.aliasSessionService.hydrate(envelope)),
        // GET /user/me returns the standard ApiResponse envelope; the profile is in `data`
        // (login stores `data.data` the same way). Unwrap it — storing the whole envelope would
        // leave the cached user without a top-level `id`/`role`, and RoleGuard bounces a
        // valid session to /login on reload.
        map((response) => response.data),
        // REPLACE the cached user with the fresh full profile. /user/me now returns the full user
        // (same shape login stores), so replacing — rather than merging — is what surfaces account
        // changes an internal admin made on the management page: the SPA previously only fetched the
        // user at login, so edits appeared only after a re-login. Guards call me() on navigation, so
        // this also keeps the cache current as the user moves around the app.
        tap((user) => setStoredUser(user))
      );
  }

  // Kept on Connect 2.0 for the forced-reset page, which admin-console temporary
  // passwords still reach (see app.routes.ts). CONNECT 1.0 deleted it with CON-202.
  // CON-157: the forced reset now sends the temporary password alongside the new one, because
  // PUT /user/password verifies it server-side before writing. The old two-argument form sent only
  // email + new password and the endpoint verified neither.
  // Merge note: this branch had already deleted the unused `encodedEmail` local that sat here on both
  // the base and test - the body sends `email?.toLowerCase()`, not the encoded form - so it is not
  // reintroduced with test's signature.
  public resetPassword(
    email: string,
    tempPassword: string,
    newPassword: string
  ): Observable<any> {
    return this.http.put(
      environment.apiUrl + '/user/password',
      {
        email: email?.toLowerCase(),
        tempPassword: tempPassword,
        newPassword: newPassword,
      },
      { responseType: 'json' }
    );
  }

  public newPassword(
    email: string,
    password: string,
    newPassword: string
  ): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http.put(
      environment.apiUrl + '/user/new-password',
      {
        email: email?.toLowerCase(),
        password: password,
        newPassword: newPassword,
      },
      { headers: headers, responseType: 'json' }
    );
  }

  // The four password-reset routes are public and send no Authorization header:
  // a browser can still hold an expired session, and a link must work there.
  // On Connect 2.0 authInterceptor still adds credentials and the CSRF header,
  // as it does for every call. The token travels only in the POST body, never
  // in a URL.

  public requestPasswordReset(email: string): Observable<PasswordResetResponse> {
    return this.http.post<PasswordResetResponse>(
      environment.apiUrl + '/user/password-reset/request',
      { email: email?.toLowerCase() }
    );
  }

  public validateResetToken(token: string): Observable<PasswordResetResponse> {
    return this.http.post<PasswordResetResponse>(
      environment.apiUrl + '/user/password-reset/validate',
      { token: token }
    );
  }

  public redeemResetToken(
    token: string,
    newPassword: string
  ): Observable<PasswordResetResponse<RedeemedAccount>> {
    return this.http.post<PasswordResetResponse<RedeemedAccount>>(
      environment.apiUrl + '/user/password-reset/redeem',
      { token: token, newPassword: newPassword }
    );
  }

  public resendResetLink(token: string): Observable<PasswordResetResponse> {
    return this.http.post<PasswordResetResponse>(
      environment.apiUrl + '/user/password-reset/resend',
      { token: token }
    );
  }

  /**
   * Canonical logout. Revokes the session server-side (POST /user/logout clears
   * the httpOnly access/refresh cookies and the readable `connect_auth` hint),
   * then clears client caches and routes to /login via the router — no
   * localStorage wipe + full page reload. Best-effort: if the server call fails
   * (e.g. the session is already gone), the client still tears down and
   * navigates. The auth interceptor performs the same server revoke on a failed
   * refresh; both funnel through this endpoint.
   */
  logout(): void {
    this.http
      .post(environment.apiUrl + '/user/logout', {}, { responseType: 'json' })
      .pipe(catchError(() => of(null)))
      .subscribe(() => this.clearClientSession());
  }

  private clearClientSession(): void {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch {
      // Storage unavailable (e.g. privacy mode) — nothing to clear.
    }
    this.appService.setRedirectUrl('');
    // Full document load (NOT router.navigate) so the whole Angular app reinitializes and every
    // root-singleton service's in-memory cache is destroyed on logout. Several services buffer the
    // signed-in user's data in ReplaySubject/BehaviorSubject caches (e.g. ProjectService), which an
    // in-app navigation would leave intact — the next user to sign in on this tab would then see the
    // previous user's data. A fresh load guarantees a clean slate; the httpOnly cookies were already
    // revoked + cleared by POST /user/logout above.
    window.location.href = '/login';
  }
}
